import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authAdminService, provisioningService, type UserSummary } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, KeyRound, Loader2, Pencil, RefreshCw, Search, Shield, Trash2, UserCog, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { MODULES } from "@/lib/rbac";

const ROLES = ["admin", "principal", "teacher", "student", "parent", "cashier", "examhead"];
const ROLE_COLOR: Record<string, string> = {
  admin: "bg-indigo-500", teacher: "bg-cyan-500", student: "bg-emerald-500",
  parent: "bg-amber-500", cashier: "bg-fuchsia-500", examhead: "bg-purple-500",
  principal: "bg-rose-500",
};

function randomPw(): string {
  const alpha = "abcdefghjkmnpqrstuvwxyz";
  const nums = "23456789";
  let p = "";
  for (let i = 0; i < 4; i++) p += alpha[Math.floor(Math.random() * alpha.length)];
  for (let i = 0; i < 4; i++) p += nums[Math.floor(Math.random() * nums.length)];
  return p;
}

export function UsersPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [reset, setReset] = useState<UserSummary | null>(null);
  const [rights, setRights] = useState<UserSummary | null>(null);
  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [deleting, setDeleting] = useState<UserSummary | null>(null);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ["users", role, q],
    queryFn: () => authAdminService.listUsers({ role: role || undefined, q: q || undefined, limit: 500 }),
    enabled: user?.role === "admin" || user?.role === "principal",
  });

  const users = listQ.data || [];
  const byRole: Record<string, number> = useMemo(() => {
    const m: Record<string, number> = {};
    users.forEach((u) => { m[u.role] = (m[u.role] || 0) + 1; });
    return m;
  }, [users]);

  if (user?.role !== "admin" && user?.role !== "principal") {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground">
        <UserCog className="h-8 w-8" />
        <div className="text-lg font-semibold">Admins only</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-7 w-7 text-primary" /> User management
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {users.length} users · {ROLES.map((r) => `${byRole[r] || 0} ${r}`).join(" · ")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateOpen(true)} data-testid="new-user-btn">
            <UserPlus className="mr-2 h-4 w-4" /> New user
          </Button>
          <Button variant="outline" onClick={() => setProvisionOpen(true)}>
            <Users className="mr-2 h-4 w-4" /> Bulk provision
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-1">
          <button className={`rounded-full border px-3 py-1 text-xs font-medium transition
            ${role === "" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
            onClick={() => setRole("")}>All</button>
          {ROLES.map((r) => (
            <button key={r} onClick={() => setRole(r)}
              className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition
                ${role === r ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}>
              <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${ROLE_COLOR[r]}`} />
              {r} <span className="ml-1 text-muted-foreground/70">({byRole[r] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isPending ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="max-h-[calc(100vh-320px)] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Class / Child</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-72 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="hover:bg-muted/50">
                      <TableCell className="font-semibold">{u.name}</TableCell>
                      <TableCell className="font-mono text-xs">{u.email}</TableCell>
                      <TableCell>
                        <Badge className="capitalize" variant="outline">
                          <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${ROLE_COLOR[u.role] || "bg-slate-400"}`} />
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {u.class_name || u.child_email || "—"}
                      </TableCell>
                      <TableCell>
                        {u.must_reset_password ? (
                          <Badge variant="outline" className="text-amber-600 border-amber-500/50">
                            <AlertTriangle className="mr-1 h-3 w-3" /> Reset pending
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-500/50">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {u.role !== "admin" && (
                          <Button size="sm" variant="ghost" data-testid={`rights-btn-${u.id}`} onClick={() => setRights(u)}>
                            <Shield className="mr-1 h-3.5 w-3.5" /> Rights
                            {u.permissions_custom && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-primary" title="Custom rights" />}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" data-testid={`edit-btn-${u.id}`} onClick={() => setEditing(u)}>
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setReset(u)}>
                          <KeyRound className="mr-1 h-3.5 w-3.5" /> Reset
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10"
                          data-testid={`delete-btn-${u.id}`} onClick={() => setDeleting(u)}>
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground p-6">No users match.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {reset && <ResetPasswordDialog user={reset} onClose={() => setReset(null)} />}
      {rights && <PermissionsDialog user={rights} onClose={() => setRights(null)} />}
      {editing && <EditUserDialog user={editing} onClose={() => setEditing(null)} />}
      {deleting && <DeleteUserDialog user={deleting} onClose={() => setDeleting(null)} />}
      {createOpen && <CreateUserDialog onClose={() => setCreateOpen(false)} />}
      {provisionOpen && <ProvisionDialog onClose={() => setProvisionOpen(false)} />}
    </div>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: UserSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const [pw, setPw] = useState<string>(randomPw());
  const [force, setForce] = useState(true);
  const [copied, setCopied] = useState(false);

  const reset = useMutation({
    mutationFn: () => authAdminService.resetPassword(user.id, pw, force),
    onSuccess: () => {
      toast.success(`Password reset for ${user.email}`);
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Reset failed"),
  });

  const copy = async () => {
    await navigator.clipboard.writeText(pw);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const canSubmit = pw.length >= 6;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" /> Reset password
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold">{user.name}</span> · <span className="font-mono text-xs">{user.email}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">New password</Label>
            <div className="flex gap-2">
              <Input value={pw} onChange={(e) => setPw(e.target.value)} className="font-mono" />
              <Button variant="outline" size="icon" onClick={() => setPw(randomPw())} title="Regenerate">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={copy}>
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">Share this password with the user through a secure channel.</p>
          </div>

          <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
            <input type="checkbox" className="mt-1" checked={force} onChange={(e) => setForce(e.target.checked)} />
            <span>
              <span className="font-medium">Force reset on next login</span>
              <span className="block text-[11px] text-muted-foreground">
                User will be prompted to set a new password on first sign-in.
              </span>
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => reset.mutate()} disabled={!canSubmit || reset.isPending}>
            {reset.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Reset password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EDITABLE_ROLES = ["admin", "principal", "teacher", "student", "parent", "cashier", "examhead"];

function genPassword(len = 12): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const symbols = "!@#$%&*?";
  const rnd = (n: number) => {
    const a = new Uint32Array(1);
    crypto.getRandomValues(a);
    return a[0] % n;
  };
  let out = "";
  for (let i = 0; i < len - 2; i++) out += chars[rnd(chars.length)];
  out += symbols[rnd(symbols.length)];
  out += String(rnd(10));
  return out;
}

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("teacher");
  const [password, setPassword] = useState(() => genPassword());
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  const create = useMutation({
    mutationFn: () => authAdminService.createUser({ name: name.trim(), email: email.trim().toLowerCase(), role, password }),
    onSuccess: () => {
      setCreated({ email: email.trim().toLowerCase(), password });
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("User created — share the temporary password");
    },
    onError: (e: any) => toast.error(e.message || "Create failed"),
  });

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied");
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" /> New user
          </DialogTitle>
          <DialogDescription>
            {created ? "Account created. Copy and share these credentials — the password won't be shown again."
                     : "Create a login account. A temporary password is generated; the user must change it on first login."}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-3" data-testid="create-user-success">
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Email</span>
                <div className="flex items-center gap-2"><span className="font-mono">{created.email}</span>
                  <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => copy(created.email)}>Copy</Button></div>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Temp password</span>
                <div className="flex items-center gap-2"><span className="font-mono" data-testid="create-user-temp-pw">{created.password}</span>
                  <Button size="sm" variant="ghost" className="h-6 px-2" data-testid="create-user-copy-pw" onClick={() => copy(created.password)}>Copy</Button></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={onClose} data-testid="create-user-done">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="create-user-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Email</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@lfs.edu" className="font-mono" data-testid="create-user-email" />
                {email && !emailOk && <p className="mt-1 text-[11px] text-destructive">Enter a valid email.</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Role</Label>
                <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="create-user-role"
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm capitalize">
                  {EDITABLE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Temporary password</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={password} onChange={(e) => setPassword(e.target.value)} className="font-mono" data-testid="create-user-password" />
                  <Button size="sm" variant="outline" onClick={() => setPassword(genPassword())} title="Regenerate" data-testid="create-user-regen">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => copy(password)} title="Copy">Copy</Button>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">User will be forced to change this on first login.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={() => create.mutate()} disabled={create.isPending || !emailOk || !name.trim() || password.length < 8} data-testid="create-user-submit">
                {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Create user
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose }: { user: UserSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role);

  const save = useMutation({
    mutationFn: () => authAdminService.updateUser(user.id, { name, email, role }),
    onSuccess: () => {
      toast.success("User updated");
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Update failed"),
  });

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" /> Edit user
          </DialogTitle>
          <DialogDescription>Update the account's name, email and role.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="font-mono" data-testid="edit-user-email" />
            {!emailOk && <p className="mt-1 text-[11px] text-destructive">Enter a valid email.</p>}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Role</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="edit-user-role"
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm capitalize">
              {EDITABLE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {role === "principal" && <p className="mt-1 text-[11px] text-muted-foreground">Principal has full access to all modules (admin-level power).</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !emailOk || !name.trim()} data-testid="edit-user-save">
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserDialog({ user, onClose }: { user: UserSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => authAdminService.deleteUser(user.id),
    onSuccess: (r) => {
      toast.success(`Deleted ${user.email}${r.staff_deactivated ? ` · staff profile deactivated` : ""}`);
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Delete failed"),
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> Delete user?
          </DialogTitle>
          <DialogDescription>
            This permanently removes the login for <b>{user.name}</b> (<span className="font-mono text-xs">{user.email}</span>).
            Any linked staff profile will be <b>deactivated</b> (kept for records), not deleted.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="delete-user-cancel">Cancel</Button>
          <Button variant="destructive" onClick={() => del.mutate()} disabled={del.isPending} data-testid="delete-user-confirm">
            {del.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            Delete user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PermissionsDialog({ user, onClose }: { user: UserSummary; onClose: () => void }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set(user.permissions || []));
  const toggle = (k: string) =>
    setSelected((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const save = useMutation({
    mutationFn: () => authAdminService.updatePermissions(user.id, Array.from(selected)),
    onSuccess: () => { toast.success("Rights updated"); qc.invalidateQueries({ queryKey: ["users"] }); onClose(); },
    onError: (e: any) => toast.error(e.message || "Failed to update rights"),
  });
  const resetDefaults = useMutation({
    mutationFn: () => authAdminService.updatePermissions(user.id, null, true),
    onSuccess: (u) => { toast.success("Reset to role defaults"); setSelected(new Set(u.permissions || [])); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e: any) => toast.error(e.message || "Failed"),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" /> Permissions / Rights Given
          </DialogTitle>
          <DialogDescription>
            {user.name} · <span className="capitalize">{user.role}</span> — tick the modules this user can access.
            Dashboard is always available. Unticking a module blocks it in the sidebar, routes and API.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          {MODULES.map((m) => (
            <label key={m.key} data-testid={`perm-${m.key}`}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm hover:bg-accent">
              <input type="checkbox" className="h-4 w-4 accent-primary"
                checked={selected.has(m.key)} onChange={() => toggle(m.key)} />
              {m.label}
            </label>
          ))}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={() => resetDefaults.mutate()} disabled={resetDefaults.isPending}>
            <RefreshCw className="mr-2 h-4 w-4" /> Reset to role defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="save-rights-btn" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save rights
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ProvisionDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [parentClass, setParentClass] = useState("");
  const [studentClasses, setStudentClasses] = useState("10,12");

  const parentMut = useMutation({
    mutationFn: () => provisioningService.parentLogins(parentClass || undefined),
    onSuccess: (r) => {
      toast.success(`Parent logins: ${r.created} created, ${r.existing} already existed (total ${r.total})`);
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: any) => toast.error(e.message || "Failed"),
  });
  const studentMut = useMutation({
    mutationFn: () => provisioningService.studentLogins(studentClasses),
    onSuccess: (r) => {
      toast.success(`Student logins: ${r.created} created, ${r.existing} already existed (total ${r.total})`);
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: any) => toast.error(e.message || "Failed"),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" /> Bulk-provision logins
          </DialogTitle>
          <DialogDescription>
            Creates login accounts using the student's GR No. as both a slug and default password.
            Users are flagged to reset on first login.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">Parent logins</div>
                <div className="text-xs text-muted-foreground">Pattern: <span className="font-mono">parent-&lt;GR&gt;@lfs.edu</span></div>
              </div>
              <Button size="sm" onClick={() => parentMut.mutate()} disabled={parentMut.isPending}>
                {parentMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Provision
              </Button>
            </div>
            <div className="mt-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Class filter (optional)</Label>
              <Input value={parentClass} onChange={(e) => setParentClass(e.target.value)} placeholder="e.g. 5  (blank = all)" />
            </div>
          </div>

          <div className="rounded-md border border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">Student logins (Class 10 & 12)</div>
                <div className="text-xs text-muted-foreground">Pattern: <span className="font-mono">student-&lt;GR&gt;@lfs.edu</span></div>
              </div>
              <Button size="sm" onClick={() => studentMut.mutate()} disabled={studentMut.isPending}>
                {studentMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Provision
              </Button>
            </div>
            <div className="mt-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Classes (comma-separated)</Label>
              <Input value={studentClasses} onChange={(e) => setStudentClasses(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
